import { lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import { QueryPersistenceProvider } from "@/admin/lib/query-persistence/query-persistence-provider";
import { queryClient } from "@/admin/lib/query-client";
import { useWarmWizardCache } from "@/admin/hooks/use-warm-wizard-cache";
import { AddressLookupProvider } from "@/shared/components/ui/address-lookup-context";
import { createAddressLookupApi } from "@/admin/hooks/use-lookups";
import { Toaster } from "sonner";

import { TOAST_DURATION } from "@/shared/lib/toast-duration";
import {
  ConfigReturnProvider,
  ConfigReturnBanner,
} from "@/admin/components/config-help";
import { LoginPage } from "@/admin/pages/login-page";
import { StaffLoginPage } from "@/admin/pages/staff-login-page";
import { PartnerLaunchpadPage } from "@/admin/pages/partner-launchpad-page";
import { PartnerDashboardPage } from "@/admin/pages/partner-dashboard-page";
import { StaffDashboardPage } from "@/admin/pages/staff-dashboard-page";
import { DashboardPage as DashboardExperiencePage } from "@/admin/pages/dashboard/dashboard-page";
import { StaffTenantDetailPage } from "@/admin/pages/staff-tenant-detail-page";
import { SettingsPage } from "@/admin/pages/settings/settings-page";
import { UsersTab } from "@/admin/pages/settings/users-tab";
import { RolesTab } from "@/admin/pages/settings/roles-tab";
import { MfaTab } from "@/admin/pages/settings/mfa-tab";
import { TranslationsTab } from "@/admin/pages/settings/translations-tab";
import { EmailTemplatesTab } from "@/admin/pages/settings/email-templates-tab";
import { FormsTab } from "@/admin/pages/settings/forms-tab";
import EmailTemplateEditor from "@/admin/pages/settings/email-authoring/email-template-editor";
import { EmailTriggersTab } from "@/admin/pages/settings/email-triggers/email-triggers-tab";
import EmailTriggerEditor from "@/admin/pages/settings/email-triggers/email-trigger-editor";
import { CommunicationsActivityPage } from "@/admin/pages/settings/communications/communications-activity-page";
// import { CertificateTemplatesTab } from '@/admin/pages/settings/certificate-templates-tab'
import { CompanyTab } from "@/admin/pages/settings/company-tab";
import { OrgSettingsTab } from "@/admin/pages/settings/org-settings-tab";
import { WelcomePageTab } from "@/admin/pages/settings/welcome-page-tab";
import { RegistrationSettingsTab } from "@/admin/pages/settings/registration-settings-tab";
import { ConsentSettingsTab } from "@/admin/pages/settings/consent-settings-tab";
import { IdentifiersSettingsTab } from "@/admin/pages/settings/identifiers-settings-tab";
import { ApiKeysTab } from "@/admin/pages/settings/api-keys-tab";
import { PulseEventsCatalogPage } from "@/admin/pages/settings/pulse-events-catalog-page";
import { PulseSubscriptionsTab } from "@/admin/pages/settings/pulse-subscriptions-tab";
import { PulseWebhookCreatePage } from "@/admin/pages/settings/pulse-webhook-create-page";
import { PulseWebhookDetailPage } from "@/admin/pages/settings/pulse-webhook-detail-page";
import { ApiLogsTab } from "@/admin/pages/settings/api-logs-tab";
import { ExceptionsTab } from "@/admin/pages/settings/exceptions-tab";
import { ClaimsSettingsTab } from "@/admin/pages/settings/claims-settings-tab";
import { GeographyLandingPage } from "@/admin/pages/manage/geography/geography-landing";
import { ZonesSection } from "@/admin/pages/manage/geography/zones-section";
import { PlantsSection } from "@/admin/pages/manage/geography/plants-section";
import { PsaMappingsSection } from "@/admin/pages/manage/geography/psa-mappings-section";
import { PostalCodesSection } from "@/admin/pages/manage/geography/postal-codes-section";
import { UsReferenceSection } from "@/admin/pages/manage/geography/us-reference-section";
import { AvailabilitySection } from "@/admin/pages/manage/geography/availability-section";
import { AssignmentsSection } from "@/admin/pages/manage/geography/assignments-section";
import { LookupSection } from "@/admin/pages/manage/geography/lookup-section";
import { ClaimFieldVisibilityRulesSection } from "@/admin/pages/settings/field-visibility-rules-section";
import { ClaimApprovalsTab } from "@/admin/pages/settings/claim-approvals-tab";
import { ClaimRoutingTab } from "@/admin/pages/settings/claim-routing/claim-routing-tab";
import { VoucherProgramsTab } from "@/admin/pages/settings/voucher-programs-tab";
import { RemindersSettingsPage } from "@/admin/pages/settings/reminders-settings-page";
import { ConsumerClaimsTab } from "@/admin/pages/settings/consumer-claims-tab";
import {
  PaymentTypesTab,
  UnitsOfMeasureTab,
  ClaimLabelsTab,
  EscalationReasonsTab,
} from "@/admin/pages/settings/claim-config-tabs";
import { DevelopersPage } from "@/admin/pages/developers/developers-page";
import { IntegrationsTab } from "@/admin/pages/settings/integrations-tab";
import { RewardRulesTab } from "@/admin/pages/settings/rewards-rules-tab";
import { RewardsIssuancesTab } from "@/admin/pages/settings/rewards-issuances-tab";
import { ProductLibraryPage } from "@/admin/pages/products/library-page";
import { PricingPage } from "@/admin/pages/manage/pricing-page";
import { ProductDetailPage } from "@/admin/pages/products/product-detail-page";
import { RegistrationsPage } from "@/admin/pages/registrations/registrations-page";
import { RegistrationDetailRouterPage } from "@/admin/pages/registrations/registration-detail-page";
import { WarrantyRecordPage } from "@/admin/pages/registrations/warranty-record-page";
import { NewRegistrationPage } from "@/admin/pages/registrations/new-registration-page";
import { HoaCommunitiesPage } from "@/admin/pages/hoa-communities/hoa-communities-page";
import { RegisterHoaCommunityPage } from "@/admin/pages/hoa-communities/register-hoa-community-page";
import { CustomersPage } from "@/admin/pages/customers/customers-page";
import { CustomerDetailPage } from "@/admin/pages/customers/customer-detail-page";
import { ProductTemplatesPage } from "@/admin/pages/manage/product-templates-page";
import { HostTemplatesPage } from "@/admin/pages/manage/host-templates-page";
import { CustomerSettingsPage } from "@/admin/pages/manage/customers-page";
import { TransactionTemplatesPage } from "@/admin/pages/manage/transaction-templates-page";
import { ProductCategoriesPage } from "@/admin/pages/manage/product-categories-page";
import { ProductBrandsPage } from "@/admin/pages/manage/product-brands-page";
import { PartsPage } from "@/admin/pages/manage/parts-page";
import { ProductFilteringPage } from "@/admin/pages/manage/product-filtering-page";
import { WarrantyClassesPage } from "@/admin/pages/manage/warranty-classes-page";
import { WarrantyMappingsPage } from "@/admin/pages/manage/warranty-mappings-page";
import { WarrantyPoliciesPage } from "@/admin/pages/manage/warranty-policies-page";
import { WarrantyPolicyDetailPage } from "@/admin/pages/manage/warranty-policies/policy-detail-page";
import { WarrantyClassDetailPage } from "@/admin/pages/manage/warranty-class-detail-page";
const WarrantyClassOverviewPage = lazy(() =>
  import("@/admin/pages/manage/warranty-class-detail/overview-page").then(
    (m) => ({
      default: m.WarrantyClassOverviewPage,
    }),
  ),
);
const WarrantyClassTermsPage = lazy(() =>
  import("@/admin/pages/manage/warranty-class-detail/terms-page").then((m) => ({
    default: m.WarrantyClassTermsPage,
  })),
);
const WarrantyClassAttributesPage = lazy(() =>
  import("@/admin/pages/manage/warranty-class-detail/attributes-page").then(
    (m) => ({
      default: m.WarrantyClassAttributesPage,
    }),
  ),
);
const WarrantyClassDisplayPage = lazy(() =>
  import("@/admin/pages/manage/warranty-class-detail/display-page").then(
    (m) => ({
      default: m.WarrantyClassDisplayPage,
    }),
  ),
);
const WarrantyClassRulesPage = lazy(() =>
  import("@/admin/pages/manage/warranty-class-detail/rules-page").then((m) => ({
    default: m.WarrantyClassRulesPage,
  })),
);
const WarrantyClassActionsPage = lazy(() =>
  import("@/admin/pages/manage/warranty-class-detail/actions-page").then(
    (m) => ({
      default: m.WarrantyClassActionsPage,
    }),
  ),
);
import { ProrationFormulasPage } from "@/admin/pages/manage/proration-formulas-page";
import { WarrantyFilteringPage } from "@/admin/pages/manage/warranty-filtering-page";
import { WarrantyCategoriesPage } from "@/admin/pages/manage/warranty-categories-page";
import { WarrantyTermsPage } from "@/admin/pages/manage/warranty-terms-page";
import { ProductWizardPage } from "@/admin/pages/manage/product-wizard-page";
import { ExternalRolesTab } from "@/admin/pages/settings/external-roles-tab";
import { PartnershipsPage } from "@/admin/pages/partnerships/partnerships-page";
import { PartnerPeoplePage } from "@/admin/pages/network/partner-people-page";
import { PartnerPersonPage } from "@/admin/pages/network/partner-person-page";
import { PartnershipDetailPage } from "@/admin/pages/partnerships/partnership-detail-page";
import { MergePartnersPage } from "@/admin/pages/partnerships/merge-partners-page";
import { PartnershipInvitePage } from "@/admin/pages/partnership-invite-page";
import { AccountMigrationPage } from "@/admin/pages/account-migration-page";
import { OrganisationInvitePage } from "@/admin/pages/organisation-invite-page";
import { ForgotPasswordPage } from "@/admin/pages/forgot-password-page";
import { ResetPasswordPage } from "@/admin/pages/reset-password-page";
import { StaffSettingsPage } from "@/admin/pages/staff/staff-settings-page";
import { StaffPulsePage } from "@/admin/pages/staff/staff-pulse-page";
import { StaffPulseDiagnosePage } from "@/admin/pages/staff/staff-pulse-diagnose-page";
import { StaffTriggerDiagnosePage } from "@/admin/pages/staff/trigger-diagnose-page";
import { EmailTemplatesPage } from "@/admin/pages/staff/email-templates-page";
import { CommunicationLogsPage } from "@/admin/pages/staff/communication-logs-page";
import { VoiceCallsPage } from "@/admin/pages/staff/voice-calls-page";
import { VoiceCallDetailPage } from "@/admin/pages/staff/voice-call-detail-page";
import { StaffPartnerDetailPage } from "@/admin/pages/staff/staff-partner-detail-page";
import { StaffConsumersPage } from "@/admin/pages/staff/staff-consumers-page";
import { CacheObservabilityPage } from "@/admin/pages/staff/cache-observability-page";
import { ClaimSchemasPage } from "@/admin/pages/manage/claim-schemas-page";
import { ClaimWorkflowsPage } from "@/admin/pages/manage/claim-workflows-page";
import { WorkflowsPage } from "@/admin/pages/manage/workflows";
import { EntityEditorPage } from "@/admin/pages/manage/entities/entity-editor-page";
import { ClaimMappingsPage } from "@/admin/pages/manage/claim-mappings-page";
import { ClaimRegistrationPage } from "@/admin/pages/manage/claim-registration-page";
import { ClaimsPage } from "@/admin/pages/claims/claims-page";
import { InspectionsPage } from "@/admin/pages/inspections/inspections-page";
import { InspectionCreateWizardPage } from "@/admin/pages/inspections/inspection-create-wizard";
import { InspectionDetailPage } from "@/admin/pages/inspections/inspection-detail-page";
import { SchedulingConsolePage } from "@/admin/pages/inspections/scheduling-console-page";
import { InspectionQueuePage } from "@/admin/pages/inspections/inspection-queue-page";
import { ItineraryDetailPage } from "@/admin/pages/inspections/itinerary-detail-page";
import { InspectionsDashboardPage } from "@/admin/pages/inspections/inspections-dashboard-page";
import { PunchListPage } from "@/admin/pages/inspections/punch-list-page";
import { ClaimDetailPage } from "@/admin/pages/claims/claim-detail-page";
import { SubmitClaimPage } from "@/admin/pages/claims/submit-claim-page";
import { MultiItemClaimPage } from "@/admin/pages/claims/multi-item-claim-page";
import { LabQueuePage } from "@/admin/pages/claims/lab-queue-page";
import { TransfersPage } from "@/admin/pages/transfers/transfers-page";
import { NewTransferPage } from "@/admin/pages/transfers/new-transfer-page";
import { TransferDetailPage } from "@/admin/pages/transfers/transfer-detail-page";
import { WorkflowIssuesPage } from "@/admin/pages/workflow-issues/workflow-issues-page";
import { ProductsLandingPage } from "@/admin/pages/manage/products-landing";
import { GuaranteesLandingPage } from "@/admin/pages/manage/guarantees-landing";
import { ClaimsLandingPage } from "@/admin/pages/manage/claims-landing";
import { NetworkLandingPage } from "@/admin/pages/network/network-landing";
import { OnboardingWizardPage } from "@/admin/pages/onboarding/onboarding-wizard-page";
import { SSOCallbackPage } from "@/admin/pages/sso-callback-page";
import { PendingApprovalsPage } from "@/admin/pages/workflows/pending-approvals-page";
import { NotFoundPage } from "@/shared/components/not-found-page";
import {
  ProtectedRoute,
  StaffProtectedRoute,
} from "@/admin/components/protected-route";
import {
  TourProvider,
  TourOverlay,
  TourWelcomeModal,
  TourHubPage,
} from "@/admin/components/tour";
import {
  FLAGS,
  useFeatureFlag,
  useFeatureFlags,
} from "@/admin/hooks/use-feature-flag";
import { useOrg } from "@/admin/hooks/use-org";
import { DevQuickLinks } from "@/admin/components/dev-quick-links";
import { ErrorBoundary } from "@/admin/components/error-boundary";
import { I18nGate } from "@/admin/components/i18n-gate";
import { ModalProvider } from "@/shared/components/modals/modal-provider";
import { AddressAutofillProvider } from "@/shared/components/ui/address-autofill-context";
import { FormattingProvider, countryToLocale } from "@/shared/lib/formatting";
import { applyTenantTheme, brandingToTheme } from "@/shared/lib/theme";
import type { ReactNode } from "react";
import { useEffect } from "react";

/**
 * SmartDashboard - routes to the correct dashboard based on context:
 * 1. Partner user not yet in context → PartnerLaunchpadPage
 * 2. Partner user in context → PartnerDashboardPage (registration-focused)
 * 3. Normal org user → DashboardPage (full tenant dashboard)
 */
/**
 * OrgFormattingProvider - bridges `useOrg()` preferences into the shared
 * `FormattingProvider`. Sits inside `QueryClientProvider` so it can read
 * the org-context query, and wraps the rest of the app so every shared
 * component (DatePicker, formatDisplayValue, etc.) sees the same locale
 * and currency.
 */
function OrgFormattingProvider({ children }: { children: ReactNode }) {
  const { preferences } = useOrg();
  const locale = countryToLocale(
    preferences?.default_country_code ?? "",
    preferences?.default_language ?? "",
  );
  const currency = preferences?.default_currency ?? "";
  const defaultCountryCode = preferences?.default_country_code ?? "";
  return (
    <FormattingProvider
      locale={locale}
      currency={currency}
      defaultCountryCode={defaultCountryCode}
      availableCurrencies={preferences?.available_currencies}
    >
      {children}
    </FormattingProvider>
  );
}

/**
 * Feeds the shared address postcode-autofill gate from the tenant's hidden
 * `postcode_autofill` flag. On by default platform-wide; a tenant opts out with
 * an explicit `false` (Teknor). Reads the raw flag map rather than
 * `useFeatureFlag` (which is fail-closed off) so absence resolves to on.
 */
function AdminAddressAutofillProvider({ children }: { children: ReactNode }) {
  const flags = useFeatureFlags();
  const enabled = flags[FLAGS.POSTCODE_AUTOFILL] !== false;
  return (
    <AddressAutofillProvider enabled={enabled}>
      {children}
    </AddressAutofillProvider>
  );
}

/**
 * TenantThemeProvider - injects the active tenant's design-token overrides
 * onto :root. Reads `tenant_theme` from the org-context query (so it follows
 * org/partner switches) and re-applies whenever it changes. Tokens the tenant
 * has not overridden fall through to the Clara Vale baseline in `index.css`.
 */
function TenantThemeProvider({ children }: { children: ReactNode }) {
  const { tenantTheme } = useOrg();
  useEffect(() => {
    applyTenantTheme(brandingToTheme({ theme: tenantTheme }));
  }, [tenantTheme]);
  return <>{children}</>;
}

function SmartDashboard() {
  const { partnerships, isPartnerAccess, isLoading } = useOrg();
  const hasPartnerships = partnerships.length > 0;
  const inPartnerContext = sessionStorage.getItem("activePartnerUuid") !== null;

  if (isLoading) return null;

  // Partner user who hasn't switched into a context yet → show launchpad
  if (hasPartnerships && !inPartnerContext && !isPartnerAccess) {
    return <PartnerLaunchpadPage />;
  }

  // Partner user operating inside a partnership → dealer dashboard.
  // `inPartnerContext` counts too: immediately after switching, org-context is
  // still refetching and `isPartnerAccess` reads false for a frame, which would
  // otherwise flash the tenant dashboard and fire its tenant-wide config call.
  if (isPartnerAccess || (hasPartnerships && inPartnerContext)) {
    return <PartnerDashboardPage />;
  }

  // Every org user gets the config-driven dashboard. It server-resolves each
  // user to their role's tiles (or the primary default), so it is the single,
  // universal home - the old legacy dashboard and its reporting-flag gate were
  // removed.
  return <DashboardExperiencePage />;
}

function GatedWarrantyRoute({ surface }: { surface: "policies" | "mappings" }) {
  const { isLoading } = useOrg();
  const policiesTabEnabled = useFeatureFlag(FLAGS.POLICIES_TAB);

  if (isLoading) return null;
  if (surface === "policies") {
    return policiesTabEnabled ? (
      <WarrantyPoliciesPage />
    ) : (
      <Navigate to="/manage/warranties/mappings" replace />
    );
  }
  return policiesTabEnabled ? (
    <Navigate to="/manage/warranties/policies" replace />
  ) : (
    <WarrantyMappingsPage />
  );
}

/**
 * Null-rendering component that runs the wizard cache warmer for the whole app
 * lifetime. Mounted once inside the query provider (so it has the QueryClient)
 * but above the router (so it is never remounted on navigation) - it reacts to
 * auth/org state changes via its own query hooks.
 */
function WizardCacheWarmer() {
  useWarmWizardCache();
  return null;
}

// Address autocomplete/resolve for every `AddressCapture` in the admin tree
// (registration wizard, transfers, inspections, entity registration, claims
// multi-item editor). Mounted once here so the shared component gets lookup
// via context on every surface.
const addressLookupApi = createAddressLookupApi(queryClient);

function App() {
  return (
    <ErrorBoundary>
      <QueryPersistenceProvider client={queryClient}>
        <WizardCacheWarmer />
        <I18nGate>
          <TenantThemeProvider>
            <OrgFormattingProvider>
              <AdminAddressAutofillProvider>
                <AddressLookupProvider api={addressLookupApi}>
                  <BrowserRouter>
                    <ConfigReturnProvider>
                      <TourProvider>
                        <ModalProvider>
                          <Routes>
                            {/* Auth */}
                            <Route path="/login" element={<LoginPage />} />
                            <Route
                              path="/:tenantSlug/login"
                              element={<LoginPage />}
                            />
                            <Route
                              path="/staff/login"
                              element={<StaffLoginPage />}
                            />
                            <Route
                              path="/forgot-password"
                              element={<ForgotPasswordPage />}
                            />
                            {/* Headless (key) link; the :uid/:token route stays for legacy emails. */}
                            <Route
                              path="/reset-password/key/:key"
                              element={<ResetPasswordPage />}
                            />
                            <Route
                              path="/reset-password/:uid/:token"
                              element={<ResetPasswordPage />}
                            />

                            {/* SSO callback */}
                            <Route
                              path="/sso/callback"
                              element={<SSOCallbackPage />}
                            />

                            {/* Public: onboarding / signup */}
                            <Route
                              path="/onboarding"
                              element={<OnboardingWizardPage />}
                            />

                            {/* Public invitation acceptance */}
                            <Route
                              path="/partnership-invite/:token"
                              element={<PartnershipInvitePage />}
                            />
                            <Route
                              path="/organization-invite/:token"
                              element={<OrganisationInvitePage />}
                            />

                            {/* Public: self-service move for a partner migrated in as inert data.
              Not a password reset - those partners have no account to reset. */}
                            <Route
                              path="/move-to-new-platform"
                              element={<AccountMigrationPage />}
                            />

                            {/* Platform guide */}
                            <Route
                              path="/guide"
                              element={
                                <ProtectedRoute>
                                  <TourHubPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* Business */}
                            <Route
                              path="/dashboard"
                              element={
                                <ProtectedRoute>
                                  <SmartDashboard />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/launchpad"
                              element={
                                <ProtectedRoute>
                                  <PartnerLaunchpadPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/workflows/pending-approvals"
                              element={
                                <ProtectedRoute>
                                  <PendingApprovalsPage />
                                </ProtectedRoute>
                              }
                            />
                            {/* Developer portal - login-gated, only renders when the
              public_api feature flag is on for the calling tenant. */}
                            <Route
                              path="/admin/developers"
                              element={
                                <ProtectedRoute flag={FLAGS.PUBLIC_API}>
                                  <DevelopersPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/settings"
                              element={
                                <ProtectedRoute permission="settings.view">
                                  <SettingsPage />
                                </ProtectedRoute>
                              }
                            >
                              <Route
                                index
                                element={
                                  <Navigate to="/settings/users" replace />
                                }
                              />
                              <Route path="users" element={<UsersTab />} />
                              <Route path="roles" element={<RolesTab />} />
                              <Route path="mfa" element={<MfaTab />} />
                              <Route
                                path="translations"
                                element={<TranslationsTab />}
                              />
                              <Route
                                path="email-templates"
                                element={
                                  <ProtectedRoute flag={FLAGS.EMAIL_AUTHORING}>
                                    <EmailTemplatesTab />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="email-templates/:id"
                                element={
                                  <ProtectedRoute flag={FLAGS.EMAIL_AUTHORING}>
                                    <EmailTemplateEditor />
                                  </ProtectedRoute>
                                }
                              />
                              <Route path="forms" element={<FormsTab />} />
                              <Route
                                path="email-triggers"
                                element={
                                  <ProtectedRoute flag={FLAGS.EMAIL_TRIGGERS}>
                                    <EmailTriggersTab />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="email-triggers/:uuid"
                                element={
                                  <ProtectedRoute flag={FLAGS.EMAIL_TRIGGERS}>
                                    <EmailTriggerEditor />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="reminders"
                                element={<RemindersSettingsPage />}
                              />
                              {/* Retired: SMS now lives in the channel-unified console. The path
              is bookmarked, so it redirects rather than 404s, carrying its SMS
              scope through ?channel. The old page was flag-gated only, so it
              also showed a nav entry to users whose API calls 403'd. */}
                              <Route
                                path="sms-activity"
                                element={
                                  <Navigate
                                    to="/settings/communications?channel=sms"
                                    replace
                                  />
                                }
                              />
                              {/* Channel-unified activity. Gated on the permission as well as the
              flag: the log carries internal correspondence, so the flag alone
              would expose it to any tenant user. */}
                              <Route
                                path="communications"
                                element={
                                  <ProtectedRoute
                                    flag={FLAGS.COMMUNICATIONS}
                                    permission="communications.view_activity"
                                  >
                                    <CommunicationsActivityPage />
                                  </ProtectedRoute>
                                }
                              />
                              {/* <Route path="certificates" element={<CertificateTemplatesTab />} /> */}
                              <Route
                                path="organisation"
                                element={<CompanyTab />}
                              />
                              <Route
                                path="welcome-page"
                                element={
                                  <ProtectedRoute adminOnly>
                                    <WelcomePageTab />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="platform"
                                element={<OrgSettingsTab />}
                              />
                              <Route
                                path="registration"
                                element={<RegistrationSettingsTab />}
                              />
                              <Route
                                path="consent"
                                element={
                                  <ProtectedRoute permission="registration-settings.manage">
                                    <ConsentSettingsTab />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="identifiers"
                                element={
                                  <ProtectedRoute flag={FLAGS.IDENTIFIERS}>
                                    <IdentifiersSettingsTab />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="claims"
                                element={
                                  <ProtectedRoute flag={FLAGS.CLAIMS}>
                                    <ClaimsSettingsTab />
                                  </ProtectedRoute>
                                }
                              >
                                <Route
                                  index
                                  element={<ClaimFieldVisibilityRulesSection />}
                                />
                                <Route
                                  path="approvals"
                                  element={
                                    <ProtectedRoute
                                      flag={FLAGS.APPROVALS}
                                      permission="claims.loa_actions"
                                    >
                                      <ClaimApprovalsTab />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="routing"
                                  element={
                                    <ProtectedRoute permission="claims.edit.global">
                                      <ClaimRoutingTab />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="reminders"
                                  element={
                                    <Navigate to="/settings/reminders" replace />
                                  }
                                />
                                <Route
                                  path="customer-claims"
                                  element={
                                    <ProtectedRoute adminOnly>
                                      <ConsumerClaimsTab />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="assignment"
                                  element={
                                    <Navigate
                                      to="/settings/claims/routing"
                                      replace
                                    />
                                  }
                                />
                                <Route
                                  path="payment-types"
                                  element={
                                    <ProtectedRoute permission="payment-types.view">
                                      <PaymentTypesTab />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="vouchers"
                                  element={
                                    <ProtectedRoute
                                      flag={FLAGS.VOUCHERS}
                                      permission="voucher.configure"
                                    >
                                      <VoucherProgramsTab />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="units-of-measure"
                                  element={
                                    <ProtectedRoute permission="units-of-measure.view">
                                      <UnitsOfMeasureTab />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="labels"
                                  element={
                                    <ProtectedRoute permission="claim-labels.view">
                                      <ClaimLabelsTab />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="escalation-reasons"
                                  element={
                                    <ProtectedRoute permission="escalation-reasons.view">
                                      <EscalationReasonsTab />
                                    </ProtectedRoute>
                                  }
                                />
                              </Route>
                              <Route path="api-keys" element={<ApiKeysTab />} />
                              <Route path="api-logs" element={<ApiLogsTab />} />
                              <Route
                                path="webhooks"
                                element={
                                  <ProtectedRoute flag={FLAGS.PULSE_WEBHOOKS}>
                                    <PulseSubscriptionsTab />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="webhooks/new"
                                element={
                                  <ProtectedRoute flag={FLAGS.PULSE_WEBHOOKS}>
                                    <PulseWebhookCreatePage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="webhooks/catalog"
                                element={
                                  <ProtectedRoute flag={FLAGS.PULSE_WEBHOOKS}>
                                    <PulseEventsCatalogPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="webhooks/:id"
                                element={
                                  <ProtectedRoute flag={FLAGS.PULSE_WEBHOOKS}>
                                    <PulseWebhookDetailPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="exceptions"
                                element={
                                  <ProtectedRoute flag={FLAGS.EXCEPTIONS}>
                                    <ExceptionsTab />
                                  </ProtectedRoute>
                                }
                              />
                              {/* Plants moved to Manage > Geography (WPS-222);
                the old settings path keeps working for bookmarks. */}
                              <Route
                                path="plants"
                                element={
                                  <Navigate to="/manage/geography/plants" replace />
                                }
                              />
                              <Route
                                path="integrations"
                                element={
                                  <ProtectedRoute flag={FLAGS.INTEGRATIONS}>
                                    <IntegrationsTab />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="rewards"
                                element={
                                  <ProtectedRoute
                                    flag={FLAGS.REWARDS}
                                    permission="rewards.view"
                                  >
                                    <RewardRulesTab />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="rewards-activity"
                                element={
                                  <ProtectedRoute
                                    flag={FLAGS.REWARDS}
                                    permission="rewards.view"
                                  >
                                    <RewardsIssuancesTab />
                                  </ProtectedRoute>
                                }
                              />
                            </Route>
                            {/* Products redirect for backward compat */}
                            <Route
                              path="/products"
                              element={
                                <Navigate to="/manage/products" replace />
                              }
                            />
                            <Route
                              path="/products/:uuid"
                              element={
                                <Navigate to="/manage/products" replace />
                              }
                            />
                            <Route
                              path="/registrations"
                              element={
                                <ProtectedRoute flag={FLAGS.REGISTRATIONS}>
                                  <RegistrationsPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/registrations/new"
                              element={
                                <ProtectedRoute flag={FLAGS.REGISTRATIONS}>
                                  <NewRegistrationPage />
                                </ProtectedRoute>
                              }
                            />
                            {/* Workflow issues - formerly the exceptions queue. The route
              path is preserved so existing bookmarks and the sidebar
              entry continue to work; the page is rebuilt on the
              ``WorkflowIssue`` API surface. */}
                            <Route
                              path="/registrations/exceptions"
                              element={
                                <ProtectedRoute flag={FLAGS.EXCEPTIONS}>
                                  <WorkflowIssuesPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/registrations/:uuid"
                              element={
                                <ProtectedRoute flag={FLAGS.REGISTRATIONS}>
                                  <RegistrationDetailRouterPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/registrations/:uuid/warranties/:warrantyUuid"
                              element={
                                <ProtectedRoute flag={FLAGS.REGISTRATIONS}>
                                  <WarrantyRecordPage />
                                </ProtectedRoute>
                              }
                            />
                            {/* HOA Communities — gated on the dedicated `hoa` capability flag */}
                            <Route
                              path="/hoa-communities"
                              element={
                                <ProtectedRoute flag={FLAGS.HOA}>
                                  <HoaCommunitiesPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/hoa-communities/new"
                              element={
                                <ProtectedRoute flag={FLAGS.HOA}>
                                  <RegisterHoaCommunityPage />
                                </ProtectedRoute>
                              }
                            />
                            {/* Claims */}
                            <Route
                              path="/claims"
                              element={
                                <ProtectedRoute
                                  flag={FLAGS.CLAIMS}
                                  anyPermission={[
                                    "claims.view.global",
                                    "claims.view.acl",
                                  ]}
                                >
                                  <ClaimsPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/claims/submit"
                              element={
                                <ProtectedRoute
                                  flag={FLAGS.CLAIMS}
                                  anyPermission={[
                                    "claims.view.global",
                                    "claims.view.acl",
                                  ]}
                                >
                                  <SubmitClaimPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/claims/submit-multi"
                              element={
                                <ProtectedRoute
                                  flag={FLAGS.MULTI_ITEM_CLAIMS}
                                  anyPermission={[
                                    "claims.view.global",
                                    "claims.view.acl",
                                  ]}
                                >
                                  <MultiItemClaimPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/claims/:uuid"
                              element={
                                <ProtectedRoute
                                  flag={FLAGS.CLAIMS}
                                  anyPermission={[
                                    "claims.view.global",
                                    "claims.view.acl",
                                  ]}
                                >
                                  <ClaimDetailPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* Inspections - field services module (gated by `inspections` flag) */}
                            <Route
                              path="/inspections"
                              element={
                                <ProtectedRoute flag={FLAGS.INSPECTIONS}>
                                  <InspectionsPage />
                                </ProtectedRoute>
                              }
                            />
                            {/* Smart Schedule surfaces gate on `scheduling`, not `inspections` - a
              tenant can run inspections one at a time without ever generating a
              routed trip. `scheduling` implies `inspections` (see the flag's
              definition + the 0096 backfill), so a single flag check is enough. */}
                            <Route
                              path="/inspections/scheduling"
                              element={
                                <ProtectedRoute flag={FLAGS.SCHEDULING}>
                                  <SchedulingConsolePage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/inspections/queue"
                              element={
                                <ProtectedRoute flag={FLAGS.SCHEDULING}>
                                  <InspectionQueuePage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/inspections/dashboard"
                              element={
                                <ProtectedRoute flag={FLAGS.INSPECTIONS}>
                                  <InspectionsDashboardPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/inspections/punch-list"
                              element={
                                <ProtectedRoute flag={FLAGS.INSPECTIONS}>
                                  <PunchListPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/inspections/new"
                              element={
                                <ProtectedRoute flag={FLAGS.INSPECTIONS}>
                                  <InspectionCreateWizardPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/inspections/itineraries/:uuid"
                              element={
                                <ProtectedRoute flag={FLAGS.SCHEDULING}>
                                  <ItineraryDetailPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/inspections/:uuid"
                              element={
                                <ProtectedRoute flag={FLAGS.INSPECTIONS}>
                                  <InspectionDetailPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* Lab */}
                            <Route
                              path="/lab"
                              element={
                                <ProtectedRoute flag={FLAGS.LAB}>
                                  <LabQueuePage />
                                </ProtectedRoute>
                              }
                            />

                            {/* Transfers */}
                            <Route
                              path="/transfers"
                              element={
                                <ProtectedRoute flag={FLAGS.TRANSFERS}>
                                  <TransfersPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/transfers/new"
                              element={
                                <ProtectedRoute flag={FLAGS.TRANSFERS}>
                                  <NewTransferPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/transfers/:uuid"
                              element={
                                <ProtectedRoute flag={FLAGS.TRANSFERS}>
                                  <TransferDetailPage />
                                </ProtectedRoute>
                              }
                            />

                            <Route
                              path="/manage"
                              element={
                                <Navigate to="/manage/products" replace />
                              }
                            />

                            {/* Products - tabbed landing */}
                            <Route
                              path="/manage/products"
                              element={
                                <ProtectedRoute
                                  flag={FLAGS.PRODUCTS}
                                  internalOnly
                                >
                                  <ProductsLandingPage />
                                </ProtectedRoute>
                              }
                            >
                              <Route
                                index
                                element={
                                  <Navigate
                                    to="/manage/products/library"
                                    replace
                                  />
                                }
                              />
                              <Route
                                path="library"
                                element={<ProductLibraryPage />}
                              />
                              {/* Pricing (WPS-162). Tab visibility is entity-gated in
                products-landing.tsx; the page's hooks + the API 404 for tenants
                without the pricing entity, so it never bleeds to others. */}
                              <Route path="pricing" element={<PricingPage />} />
                              <Route
                                path="templates"
                                element={<ProductTemplatesPage />}
                              />
                              <Route
                                path="host-templates"
                                element={<HostTemplatesPage />}
                              />
                              <Route
                                path="customers"
                                element={<CustomerSettingsPage />}
                              />
                              <Route
                                path="transaction-templates"
                                element={<TransactionTemplatesPage />}
                              />
                              <Route
                                path="categories"
                                element={<ProductCategoriesPage />}
                              />
                              <Route
                                path="brands"
                                element={
                                  <ProtectedRoute flag={FLAGS.BRANDS}>
                                    <ProductBrandsPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route path="parts" element={<PartsPage />} />
                              <Route
                                path="filtering"
                                element={<ProductFilteringPage />}
                              />
                              {/* Geo Zones moved to Manage > Geography (WPS-222);
                the old products path keeps working for bookmarks + the tour. */}
                              <Route
                                path="geo-zones"
                                element={
                                  <Navigate to="/manage/geography/zones" replace />
                                }
                              />
                              <Route
                                path="wizard"
                                element={<ProductWizardPage />}
                              />
                            </Route>
                            {/* Geography (WPS-222): the consolidated geographic
                reference-data admin. Gated on the geo capability flag; each
                section also carries its own codename. */}
                            <Route
                              path="/manage/geography"
                              element={
                                <ProtectedRoute
                                  flag={FLAGS.GEOGRAPHIC_AVAILABILITY}
                                  internalOnly
                                  anyPermission={[
                                    "geo-zones.view",
                                    "geo-assignments.view",
                                  ]}
                                >
                                  <GeographyLandingPage />
                                </ProtectedRoute>
                              }
                            >
                              <Route
                                path="zones"
                                element={
                                  <ProtectedRoute permission="geo-zones.view">
                                    <ZonesSection />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="plants"
                                element={
                                  <ProtectedRoute permission="geo-zones.view">
                                    <PlantsSection />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="psa"
                                element={
                                  <ProtectedRoute permission="geo-zones.view">
                                    <PsaMappingsSection />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="postal-codes"
                                element={
                                  <ProtectedRoute permission="geo-zones.view">
                                    <PostalCodesSection />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="reference"
                                element={
                                  <ProtectedRoute permission="geo-zones.view">
                                    <UsReferenceSection />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="availability"
                                element={
                                  <ProtectedRoute permission="products.view.global">
                                    <AvailabilitySection />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="assignments"
                                element={
                                  <ProtectedRoute permission="geo-assignments.view">
                                    <AssignmentsSection />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="lookup"
                                element={
                                  <ProtectedRoute permission="geo-zones.view">
                                    <LookupSection />
                                  </ProtectedRoute>
                                }
                              />
                            </Route>
                            <Route
                              path="/manage/products/:uuid"
                              element={
                                <ProtectedRoute
                                  flag={FLAGS.PRODUCTS}
                                  internalOnly
                                >
                                  <ProductDetailPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* Warranties - tabbed landing */}
                            <Route
                              path="/manage/warranties"
                              element={
                                <ProtectedRoute internalOnly>
                                  <GuaranteesLandingPage />
                                </ProtectedRoute>
                              }
                            >
                              <Route
                                index
                                element={
                                  <Navigate
                                    to="/manage/warranties/templates"
                                    replace
                                  />
                                }
                              />
                              <Route
                                path="templates"
                                element={<WarrantyClassesPage />}
                              />
                              <Route
                                path="policies"
                                element={
                                  <GatedWarrantyRoute surface="policies" />
                                }
                              />
                              <Route
                                path="mappings"
                                element={
                                  <GatedWarrantyRoute surface="mappings" />
                                }
                              />
                              <Route
                                path="proration"
                                element={
                                  <ProtectedRoute
                                    flag={FLAGS.PRORATION_FORMULAS}
                                  >
                                    <ProrationFormulasPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="rules"
                                element={
                                  <Navigate
                                    to="/manage/warranties/policies"
                                    replace
                                  />
                                }
                              />
                              <Route
                                path="filtering"
                                element={<WarrantyFilteringPage />}
                              />
                              <Route
                                path="categories"
                                element={<WarrantyCategoriesPage />}
                              />
                              <Route
                                path="terms"
                                element={<WarrantyTermsPage />}
                              />
                            </Route>
                            <Route
                              path="/manage/warranties/templates/:uuid"
                              element={
                                <ProtectedRoute>
                                  <WarrantyClassDetailPage />
                                </ProtectedRoute>
                              }
                            >
                              <Route
                                index
                                element={<Navigate to="overview" replace />}
                              />
                              <Route
                                path="overview"
                                element={<WarrantyClassOverviewPage />}
                              />
                              <Route
                                path="attributes"
                                element={<WarrantyClassAttributesPage />}
                              />
                              <Route
                                path="display"
                                element={<WarrantyClassDisplayPage />}
                              />
                              <Route
                                path="terms"
                                element={<WarrantyClassTermsPage />}
                              />
                              <Route
                                path="rules"
                                element={<WarrantyClassRulesPage />}
                              />
                              <Route
                                path="actions"
                                element={<WarrantyClassActionsPage />}
                              />
                            </Route>
                            <Route
                              path="/manage/warranties/policies/:policyCode"
                              element={
                                <ProtectedRoute>
                                  <WarrantyPolicyDetailPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* Claims config - tabbed landing. The workflows + mappings
              tabs render the original Ruby-era admin UI, but the data
              source under the hood is the generic apps.workflows engine
              (claim-workflow hooks adapted to /api/workflows/admin/
              definitions/...). */}
                            <Route
                              path="/manage/claims"
                              element={
                                <ProtectedRoute
                                  flag={FLAGS.CLAIMS}
                                  internalOnly
                                >
                                  <ClaimsLandingPage />
                                </ProtectedRoute>
                              }
                            >
                              <Route
                                index
                                element={
                                  <Navigate
                                    to="/manage/claims/schemas"
                                    replace
                                  />
                                }
                              />
                              <Route
                                path="schemas"
                                element={<ClaimSchemasPage />}
                              />
                              <Route
                                path="workflows"
                                element={<ClaimWorkflowsPage />}
                              />
                              <Route
                                path="mappings"
                                element={<ClaimMappingsPage />}
                              />
                              <Route
                                path="registration"
                                element={<ClaimRegistrationPage />}
                              />
                            </Route>

                            {/* Network - tabbed landing */}
                            <Route
                              path="/network"
                              element={
                                <ProtectedRoute>
                                  <NetworkLandingPage />
                                </ProtectedRoute>
                              }
                            >
                              <Route
                                index
                                element={
                                  <Navigate to="/network/partners" replace />
                                }
                              />
                              <Route
                                path="partners"
                                element={<PartnershipsPage />}
                              />
                              <Route
                                path="people"
                                element={
                                  <ProtectedRoute permission="users.manage">
                                    <PartnerPeoplePage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="customers"
                                element={<CustomersPage />}
                              />
                              <Route
                                path="external-roles"
                                element={<ExternalRolesTab />}
                              />
                            </Route>
                            <Route
                              path="/network/people/:uuid"
                              element={
                                <ProtectedRoute permission="users.manage">
                                  <PartnerPersonPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/network/:uuid/merge"
                              element={
                                <ProtectedRoute>
                                  <MergePartnersPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/network/:uuid"
                              element={
                                <ProtectedRoute>
                                  <PartnershipDetailPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* Redirects from old flat URLs */}
                            <Route
                              path="/manage/product-templates"
                              element={
                                <Navigate
                                  to="/manage/products/templates"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/manage/host-templates"
                              element={
                                <Navigate
                                  to="/manage/products/host-templates"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/manage/transaction-templates"
                              element={
                                <Navigate
                                  to="/manage/products/transaction-templates"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/manage/product-categories"
                              element={
                                <Navigate
                                  to="/manage/products/categories"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/manage/product-wizard"
                              element={
                                <Navigate
                                  to="/manage/products/wizard"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/manage/product-filtering"
                              element={
                                <Navigate
                                  to="/manage/products/filtering"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/manage/warranty-classes"
                              element={
                                <Navigate
                                  to="/manage/warranties/templates"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/manage/warranty-classes/:uuid"
                              element={
                                <ProtectedRoute>
                                  <WarrantyClassDetailPage />
                                </ProtectedRoute>
                              }
                            >
                              <Route
                                index
                                element={<Navigate to="overview" replace />}
                              />
                              <Route
                                path="overview"
                                element={<WarrantyClassOverviewPage />}
                              />
                              <Route
                                path="terms"
                                element={<WarrantyClassTermsPage />}
                              />
                              <Route
                                path="attributes"
                                element={<WarrantyClassAttributesPage />}
                              />
                              <Route
                                path="rules"
                                element={<WarrantyClassRulesPage />}
                              />
                            </Route>
                            <Route
                              path="/manage/proration-formulas"
                              element={
                                <Navigate
                                  to="/manage/warranties/proration"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/manage/warranty-mappings"
                              element={
                                <Navigate
                                  to="/manage/warranties/mappings"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/manage/guarantees/*"
                              element={
                                <Navigate
                                  to="/manage/warranties/templates"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/manage/claim-schemas"
                              element={
                                <Navigate to="/manage/claims/schemas" replace />
                              }
                            />
                            <Route
                              path="/manage/claim-workflows"
                              element={
                                <Navigate
                                  to="/manage/claims/workflows"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/manage/workflows/entities"
                              element={
                                <ProtectedRoute
                                  flag={FLAGS.WORKFLOW_DESIGNER}
                                  internalOnly
                                >
                                  <EntityEditorPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/manage/workflows"
                              element={
                                <ProtectedRoute
                                  flag={FLAGS.WORKFLOW_DESIGNER}
                                  internalOnly
                                >
                                  <WorkflowsPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/manage/claim-mappings"
                              element={
                                <Navigate
                                  to="/manage/claims/mappings"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/manage/partnerships"
                              element={
                                <Navigate to="/network/partners" replace />
                              }
                            />
                            <Route
                              path="/manage/partnerships/:uuid"
                              element={
                                <ProtectedRoute internalOnly>
                                  <PartnershipDetailPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/network/partnerships"
                              element={
                                <Navigate to="/network/partners" replace />
                              }
                            />
                            <Route
                              path="/manage/external-roles"
                              element={
                                <Navigate
                                  to="/network/external-roles"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/customers"
                              element={
                                <Navigate to="/network/customers" replace />
                              }
                            />
                            <Route
                              path="/customers/:uuid"
                              element={
                                <ProtectedRoute>
                                  <CustomerDetailPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* Staff */}
                            <Route
                              path="/staff/dashboard"
                              element={
                                <StaffProtectedRoute>
                                  <StaffDashboardPage />
                                </StaffProtectedRoute>
                              }
                            />
                            <Route
                              path="/staff/tenants/:uuid"
                              element={
                                <StaffProtectedRoute>
                                  <StaffTenantDetailPage />
                                </StaffProtectedRoute>
                              }
                            />
                            <Route
                              path="/staff/consumers"
                              element={
                                <StaffProtectedRoute>
                                  <StaffConsumersPage />
                                </StaffProtectedRoute>
                              }
                            />
                            <Route
                              path="/staff/partners/:uuid"
                              element={
                                <StaffProtectedRoute>
                                  <StaffPartnerDetailPage />
                                </StaffProtectedRoute>
                              }
                            />
                            <Route
                              path="/staff/onboarding/new"
                              element={
                                <StaffProtectedRoute>
                                  <OnboardingWizardPage />
                                </StaffProtectedRoute>
                              }
                            />
                            <Route
                              path="/staff/email-templates"
                              element={
                                <StaffProtectedRoute>
                                  <EmailTemplatesPage />
                                </StaffProtectedRoute>
                              }
                            />
                            <Route
                              path="/staff/communication-logs"
                              element={
                                <StaffProtectedRoute>
                                  <CommunicationLogsPage />
                                </StaffProtectedRoute>
                              }
                            />
                            <Route
                              path="/staff/voice-calls"
                              element={
                                <StaffProtectedRoute>
                                  <VoiceCallsPage />
                                </StaffProtectedRoute>
                              }
                            />
                            <Route
                              path="/staff/voice-calls/:uuid"
                              element={
                                <StaffProtectedRoute>
                                  <VoiceCallDetailPage />
                                </StaffProtectedRoute>
                              }
                            />
                            <Route
                              path="/staff/triggers/diagnose"
                              element={
                                <StaffProtectedRoute>
                                  <StaffTriggerDiagnosePage />
                                </StaffProtectedRoute>
                              }
                            />
                            <Route
                              path="/staff/pulse"
                              element={
                                <StaffProtectedRoute>
                                  <StaffPulsePage />
                                </StaffProtectedRoute>
                              }
                            />
                            <Route
                              path="/staff/pulse/diagnose"
                              element={
                                <StaffProtectedRoute>
                                  <StaffPulseDiagnosePage />
                                </StaffProtectedRoute>
                              }
                            />
                            <Route
                              path="/staff/cache"
                              element={
                                <StaffProtectedRoute>
                                  <CacheObservabilityPage />
                                </StaffProtectedRoute>
                              }
                            />
                            <Route
                              path="/staff/settings"
                              element={
                                <StaffProtectedRoute>
                                  <StaffSettingsPage />
                                </StaffProtectedRoute>
                              }
                            >
                              <Route
                                index
                                element={
                                  <Navigate
                                    to="/staff/settings/translations"
                                    replace
                                  />
                                }
                              />
                              <Route
                                path="translations"
                                element={<TranslationsTab staff />}
                              />
                            </Route>

                            {/* Dev-only: unprotected wizard preview */}
                            {import.meta.env.DEV && (
                              <Route
                                path="/dev/onboarding-wizard"
                                element={<OnboardingWizardPage />}
                              />
                            )}

                            {/* Default */}
                            <Route
                              path="/"
                              element={<Navigate to="/login" />}
                            />
                            <Route path="*" element={<NotFoundPage />} />
                          </Routes>
                          <TourOverlay />
                          <TourWelcomeModal />
                          <ConfigReturnBanner />
                          {import.meta.env.DEV && <DevQuickLinks />}
                        </ModalProvider>
                      </TourProvider>
                    </ConfigReturnProvider>
                  </BrowserRouter>
                </AddressLookupProvider>
              </AdminAddressAutofillProvider>
            </OrgFormattingProvider>
          </TenantThemeProvider>
          {/* WPS-97: 3s was long enough to confirm a save but not to read - let
          alone act on - a message explaining why an action failed. Errors
          raised through `toastApiError` override this with a persistent
          duration; this default covers the rest. */}
          <Toaster
            position="top-right"
            duration={TOAST_DURATION.success}
            closeButton
          />
        </I18nGate>
      </QueryPersistenceProvider>
    </ErrorBoundary>
  );
}

export default App;
